import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Car, Bike, Truck, TrendingUp, Info, ChevronRight, Loader2, Check, X, Search } from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { useStore } from '@/contexts/StoreContext';
import { useTheme } from '@/contexts/ThemeContext';
import { API_FIPE } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useIsLayout4 } from '@/hooks/useIsLayout4';
import { layout4ThemeStyle } from '@/layouts/layout-4/theme';
import '@/layouts/layout-4/styles.css';

interface FipeTableProps {
  service: any;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  serviceImage?: string;
  serviceTitle?: string;
}

interface FipeData {
  brand: { _id: string | null; label: string | null };
  model: { _id: string | null; label: string | null };
  year: { _id: string | null; label: string | null };
  price?: string;
}

export const FipeTable = ({ service, isOpen, onOpenChange, serviceImage, serviceTitle }: FipeTableProps) => {
  const { config } = useAppData();
  const { colors } = useTheme();
  const isLayout4 = useIsLayout4();
  const modalThemeStyle = isLayout4 ? layout4ThemeStyle(colors) : undefined;
  const { selectedStore } = useStore();
  const store = selectedStore || (config?.stores && config.stores.length > 0 ? config.stores[0] : null);

  // Safely get colors with defaults
  const safeColors = colors || { title: '#000000', button: '#000000', primary: '#000000', secondary: '#000000', text: '#000000' };
  const titleColor = safeColors.title;

  const dialogContentClass = isLayout4
    ? 'layout-4 wl4-service-modal max-w-md w-full h-full md:max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden'
    : 'max-w-md w-full h-full md:max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden';

  const bodyClass = isLayout4
    ? 'wl4-service-modal-body'
    : 'flex-1 overflow-y-auto px-6 py-6 space-y-4';

  const stepButtonClass = (disabled = false) =>
    isLayout4
      ? 'wl4-fipe-step'
      : `w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors text-left${disabled ? ' disabled:opacity-50 disabled:cursor-not-allowed' : ''}`;

  const stepLabelClass = isLayout4 ? 'wl4-fipe-step-label' : 'text-sm font-medium';
  const stepValueClass = isLayout4 ? 'wl4-fipe-step-value' : 'text-sm text-muted-foreground mt-1';
  const chevronClass = isLayout4 ? 'h-5 w-5 text-[var(--wl4-muted)]' : 'h-5 w-5 text-muted-foreground';

  // Safely get data options from service
  const dataOptions = (service?.options && Array.isArray(service.options))
    ? service.options.map((item: any) => item?.title?.toLowerCase()).filter(Boolean)
    : ['carro'];

  const availableTypes = [];

  if (dataOptions.find((item: string) => item === 'carro')) availableTypes.push({ label: 'Carro', value: 1, icon: Car });
  if (dataOptions.find((item: string) => item === 'moto')) availableTypes.push({ label: 'Moto', value: 2, icon: Bike });
  if (dataOptions.find((item: string) => item === 'caminhão' || item === 'caminhao')) availableTypes.push({ label: 'Caminhão', value: 3, icon: Truck });

  const [vehicleType, setVehicleType] = useState(availableTypes[0]?.value || 1);
  const [data, setData] = useState<FipeData>({
    brand: { _id: null, label: null },
    model: { _id: null, label: null },
    year: { _id: null, label: null },
  });
  const [fipeResult, setFipeResult] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorField, setSelectorField] = useState<'brand' | 'model' | 'year'>('brand');

  useEffect(() => {
    const fetchFipeData = async () => {
      // Don't fetch if store is not available
      if (!store) {
        return;
      }

      if (data.year._id !== null && data.brand._id && data.model._id) {
        setIsLoading(true);
        try {
          const response = await API_FIPE.get(
            store,
            String(vehicleType),
            data.brand._id,
            data.model._id,
            data.year._id
          );

          // Response comes as { status: 200, data: { fipeCode, monthRef, price } }
          if (response && response.status === 200 && response.data) {
            setFipeResult(response.data);
            setData(prev => ({ ...prev, price: response.data.price }));
          }
        } catch (error) {
          console.error('❌ Error fetching FIPE data:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Reset result if year is not selected
        setFipeResult(null);
      }
    };

    fetchFipeData();
  }, [data.year._id, data.brand._id, data.model._id, vehicleType, store]);

  const handleVehicleTypeChange = (newType: number) => {
    setVehicleType(newType);
    setData({
      brand: { _id: null, label: null },
      model: { _id: null, label: null },
      year: { _id: null, label: null },
    });
    setFipeResult(null);
  };

  const handleFieldSelect = (field: 'brand' | 'model' | 'year') => {
    setSelectorField(field);
    setSelectorOpen(true);
  };

  const handleValueSelect = (field: 'brand' | 'model' | 'year', value: { _id: string; label: string }) => {
    if (field === 'brand') {
      setData({
        brand: value,
        model: { _id: null, label: null },
        year: { _id: null, label: null },
      });
    } else if (field === 'model') {
      setData(prev => ({
        ...prev,
        model: value,
        year: { _id: null, label: null },
      }));
    } else {
      setData(prev => ({
        ...prev,
        year: value,
      }));
    }
    setSelectorOpen(false);
  };

  // Don't render if store is not available
  if (!store) {
    return null;
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className={dialogContentClass} style={modalThemeStyle}>
          <DialogTitle className="sr-only">
            {serviceTitle || 'Tabela FIPE'}
          </DialogTitle>

          {isLayout4 ? (
            <div className="wl4-service-modal-head">
              <div className="wl4-service-modal-head-icon">
                <TrendingUp className="h-5 w-5" />
              </div>
              <div className="wl4-service-modal-head-text">
                <h2>{serviceTitle || 'Tabela FIPE'}</h2>
                <p>Consulte o preço de veículos</p>
              </div>
              <button
                type="button"
                className="wl4-service-modal-close"
                onClick={() => onOpenChange(false)}
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              {/* Image Header - Modern and Professional */}
              {serviceImage && (
                <div className="relative aspect-[280/100] overflow-hidden flex-shrink-0">
                  <img
                    src={serviceImage}
                    alt={serviceTitle || 'Tabela FIPE'}
                    className="w-full h-full object-cover"
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

                  {/* Close Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-4 right-4 h-10 w-10 bg-white/20 hover:bg-white/25 backdrop-blur-sm border border-white/20 text-white z-20"
                    onClick={() => onOpenChange(false)}
                  >
                    <X className="h-5 w-5 text-white" />
                  </Button>

                  {/* Title and Icon Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-white/10 backdrop-blur-sm border border-white/20">
                        <TrendingUp className="h-7 w-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-2xl font-bold text-white drop-shadow-lg">
                          {serviceTitle || 'Tabela FIPE'}
                        </h2>
                        <p className="text-sm text-white/80">
                          Consulte o preço de veículos
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Fallback Header if no image */}
              {!serviceImage && (
                <DialogHeader className="sticky top-0 z-10 bg-background border-b px-6 py-4 rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <DialogTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      <span style={{ color: titleColor }}>{serviceTitle || 'Tabela FIPE'}</span>
                    </DialogTitle>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 flex-shrink-0"
                      onClick={() => onOpenChange(false)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </DialogHeader>
              )}
            </>
          )}

          <div className={bodyClass}>
            {/* Vehicle Type Selector */}
            {availableTypes.length > 1 && (
              <div className={isLayout4 ? 'wl4-fipe-tabs mb-4' : undefined}>
                <Tabs value={String(vehicleType)} onValueChange={(v) => handleVehicleTypeChange(Number(v))}>
                  <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${availableTypes.length}, 1fr)` }}>
                    {availableTypes.map((type) => {
                      const Icon = type.icon;
                      if (!Icon || typeof type.value !== 'number' || !type.label) {
                        return null;
                      }
                      return (
                        <TabsTrigger key={type.value} value={String(type.value)} className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {String(type.label)}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </Tabs>
              </div>
            )}

            {/* Selection Fields */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => handleFieldSelect('brand')}
                className={stepButtonClass()}
              >
                <div>
                  <p className={stepLabelClass}>Marca</p>
                  <p className={stepValueClass}>
                    {data.brand.label || 'Selecionar'}
                  </p>
                </div>
                <ChevronRight className={chevronClass} />
              </button>

              <button
                type="button"
                onClick={() => handleFieldSelect('model')}
                disabled={!data.brand._id}
                className={stepButtonClass(true)}
              >
                <div>
                  <p className={stepLabelClass}>Modelo</p>
                  <p className={stepValueClass}>
                    {data.model.label || 'Selecionar'}
                  </p>
                </div>
                <ChevronRight className={chevronClass} />
              </button>

              <button
                type="button"
                onClick={() => handleFieldSelect('year')}
                disabled={!data.model._id}
                className={stepButtonClass(true)}
              >
                <div>
                  <p className={stepLabelClass}>Ano</p>
                  <p className={stepValueClass}>
                    {data.year.label || 'Selecionar'}
                  </p>
                </div>
                <ChevronRight className={chevronClass} />
              </button>

              {/* FIPE Result */}
              {isLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className={`h-8 w-8 animate-spin ${isLayout4 ? 'text-[var(--wl4-muted)]' : 'text-muted-foreground'}`} />
                </div>
              )}

              {fipeResult && !isLoading && typeof fipeResult === 'object' && (
                isLayout4 ? (
                  <div className="wl4-fipe-result">
                    <div className="wl4-fipe-result-row">
                      <p className="wl4-fipe-step-label">Mês de referência</p>
                      <p className="wl4-fipe-step-value">{String(fipeResult.monthRef || '...')}</p>
                    </div>
                    <div className="wl4-fipe-result-row">
                      <p className="wl4-fipe-step-label">Código FIPE</p>
                      <p className="wl4-fipe-step-value">{String(fipeResult.fipeCode || '...')}</p>
                    </div>
                    <div className="wl4-fipe-result-row">
                      <p className="wl4-fipe-step-label">Preço Médio</p>
                      <p className="wl4-fipe-result-price">{String(fipeResult.price || '...')}</p>
                    </div>
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-0">
                      <div className="divide-y">
                        <div className="flex items-center justify-between p-4">
                          <p className="text-sm font-medium">Mês de referência</p>
                          <p className="text-sm text-muted-foreground">{String(fipeResult.monthRef || '...')}</p>
                        </div>
                        <div className="flex items-center justify-between p-4">
                          <p className="text-sm font-medium">Código FIPE</p>
                          <p className="text-sm text-muted-foreground">{String(fipeResult.fipeCode || '...')}</p>
                        </div>
                        <div className="flex items-center justify-between p-4">
                          <p className="text-sm font-medium">Preço Médio</p>
                          <p className="text-sm font-semibold">{String(fipeResult.price || '...')}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              )}

              {service?.warning && typeof service.warning === 'string' && (
                isLayout4 ? (
                  <p className="wl4-service-warning">{service.warning}</p>
                ) : (
                  <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <p className="text-sm text-blue-800 dark:text-blue-200">{service.warning}</p>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Footer - Fixed */}
          {fipeResult && (
            <div className={isLayout4 ? 'wl4-service-modal-footer' : 'sticky bottom-0 z-10 bg-background border-t px-6 py-4 space-y-3 rounded-b-lg'}>
              <Button
                type="button"
                variant={isLayout4 ? undefined : 'outline'}
                onClick={() => {
                  setData({
                    brand: { _id: null, label: null },
                    model: { _id: null, label: null },
                    year: { _id: null, label: null },
                  });
                  setFipeResult(null);
                }}
                className={isLayout4 ? 'wl4-service-modal-submit' : 'w-full'}
                size="lg"
              >
                Nova Consulta
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Selector Dialog */}
      {selectorOpen && (
        <FipeSelector
          isOpen={selectorOpen}
          onOpenChange={setSelectorOpen}
          field={selectorField}
          vehicleType={vehicleType}
          fieldValue={data}
          onSelect={handleValueSelect}
          serviceImage={serviceImage}
          serviceTitle={serviceTitle}
          layoutVariant={isLayout4 ? 'layout-4' : 'default'}
        />
      )}
    </>
  );
};

// FipeSelector Component
interface FipeSelectorProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  field: 'brand' | 'model' | 'year';
  vehicleType: number;
  fieldValue: FipeData;
  onSelect: (field: 'brand' | 'model' | 'year', value: { _id: string; label: string }) => void;
  serviceImage?: string;
  serviceTitle?: string;
  layoutVariant?: 'default' | 'layout-4';
}

const FipeSelector = ({
  isOpen,
  onOpenChange,
  field,
  vehicleType,
  fieldValue,
  onSelect,
  serviceImage,
  serviceTitle,
  layoutVariant = 'default',
}: FipeSelectorProps) => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const store = selectedStore || (config?.stores && config.stores.length > 0 ? config.stores[0] : null);
  const [search, setSearch] = useState('');
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { colors } = useTheme();
  const isLayout4 = layoutVariant === 'layout-4';
  const modalThemeStyle = isLayout4 ? layout4ThemeStyle(colors) : undefined;

  // Safely get colors with defaults
  const safeColors = colors || { title: '#000000', button: '#000000', primary: '#000000', secondary: '#000000', text: '#000000' };
  const primaryColor = safeColors.primary;

  const fieldLabels = {
    brand: 'Marca',
    model: 'Modelo',
    year: 'Ano'
  };

  // Get icon based on vehicle type
  const getVehicleTypeIcon = () => {
    const iconMap: Record<number, any> = {
      1: Car,    // Carro
      2: Bike,   // Moto
      3: Truck   // Caminhão
    };
    return iconMap[vehicleType] || Car;
  };

  useEffect(() => {
    const fetchData = async () => {
      // Don't fetch if store is not available
      if (!store) {
        setIsLoading(false);
        setData([]);
        return;
      }

      setIsLoading(true);
      try {
        // Determine which parameters to send based on the field being selected
        let brandId = null;
        let modelId = null;

        if (field === 'model') {
          // For models, send brand
          brandId = fieldValue.brand._id;
        } else if (field === 'year') {
          // For years, send brand and model
          brandId = fieldValue.brand._id;
          modelId = fieldValue.model._id;
        }
        // For brand, send nothing (brandId and modelId remain null)

        const response = await API_FIPE.get(
          store,
          String(vehicleType),
          brandId,
          modelId,
          null
        );

        // Response comes as { status: 200, data: [...] }
        if (response && response.status === 200 && response.data) {
          if (Array.isArray(response.data)) {
            setData(response.data);
          } else {
            setData([]);
          }
        } else {
          setData([]);
        }
      } catch (error) {
        console.error('Error fetching FIPE selector data:', error);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen, vehicleType, field, fieldValue.brand._id, fieldValue.model._id, store]);

  const filteredData = Array.isArray(data) ? data.filter(item => {
    if (!search) return true;
    return search.split(' ').every(term =>
      item.label?.toLowerCase().includes(term.trim().toLowerCase())
    );
  }) : [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          isLayout4
            ? 'layout-4 wl4-service-modal max-w-md w-full h-full md:max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden'
            : 'max-w-md w-full h-full md:max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden'
        }
        style={modalThemeStyle}
      >
        <DialogTitle className="sr-only">
          {fieldLabels[field]}
        </DialogTitle>

        {isLayout4 ? (
          <>
            <div className="wl4-service-modal-head">
              <div className="wl4-service-modal-head-icon">
                {(() => {
                  const VehicleIcon = getVehicleTypeIcon();
                  return <VehicleIcon className="h-5 w-5" />;
                })()}
              </div>
              <div className="wl4-service-modal-head-text">
                <h2>{fieldLabels[field]}</h2>
                <p>Selecione uma opção</p>
              </div>
              <button
                type="button"
                className="wl4-service-modal-close"
                onClick={() => onOpenChange(false)}
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="wl4-vehicle-search relative shrink-0">
              <Search className="absolute left-7 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--wl4-muted)] z-10" />
              <Input
                type="text"
                placeholder="Pesquisar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="wl4-service-input pl-10"
              />
            </div>
          </>
        ) : serviceImage ? (
          <div className="relative aspect-[280/100] overflow-hidden flex-shrink-0">
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 h-10 w-10 bg-black/5 hover:bg-black/10 backdrop-blur-sm border border-black/10 text-black z-20"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-5 w-5 text-black" />
            </Button>

            {/* Title and Icon Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-black/5 backdrop-blur-sm border border-black/10">
                  {(() => {
                    const VehicleIcon = getVehicleTypeIcon();
                    return <VehicleIcon className="h-7 w-7 text-black/80" />;
                  })()}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-black">
                    {fieldLabels[field]}
                  </h2>
                  <p className="text-sm text-black/80">
                    Selecione uma opção
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
            {/* Hidden DialogTitle for accessibility */}
            <DialogTitle className="sr-only">
              {fieldLabels[field]}
            </DialogTitle>

            {/* Gradient Overlay */}
            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${primaryColor}15, ${primaryColor}05)` }} />
            
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 h-10 w-10 bg-background/10 hover:bg-background/20 backdrop-blur-sm border border-border/20 z-20"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-5 w-5" />
            </Button>

            {/* Title and Icon Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-background/10 backdrop-blur-sm border border-border/20">
                  {(() => {
                    const VehicleIcon = getVehicleTypeIcon();
                    return <VehicleIcon className="h-7 w-7" style={{ color: primaryColor }} />;
                  })()}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-foreground">
                    {fieldLabels[field]}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Selecione uma opção
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isLayout4 && (
        <div className="px-6 pb-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Pesquisar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        )}

        {/* List */}
        <div className={isLayout4 ? 'wl4-service-modal-body !pt-0 flex-1 overflow-y-auto min-h-0' : 'flex-1 overflow-y-auto'}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className={`h-8 w-8 animate-spin ${isLayout4 ? 'text-[var(--wl4-muted)]' : 'text-muted-foreground'}`} />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-6">
              <p className={isLayout4 ? 'text-[var(--wl4-muted)]' : 'text-muted-foreground'}>
                {search ? 'Nenhum resultado encontrado' : 'Nenhum dado disponível'}
              </p>
            </div>
          ) : (
            <div className={isLayout4 ? undefined : 'divide-y'}>
              {filteredData.map((item, index) => {
                const isSelected =
                  (field === 'brand' && fieldValue.brand._id === item._id) ||
                  (field === 'model' && fieldValue.model._id === item._id) ||
                  (field === 'year' && fieldValue.year._id === item._id);

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => onSelect(field, { _id: item._id, label: item.label })}
                    className={
                      isLayout4
                        ? `wl4-vehicle-list-item wl4-fipe-option-item ${isSelected ? 'is-selected' : ''}`
                        : `w-full text-left px-6 py-3 hover:bg-muted/50 transition-colors flex items-center justify-between ${isSelected ? 'bg-primary/5' : ''}`
                    }
                  >
                    <p className={`text-sm ${isSelected ? 'font-semibold' : 'font-medium'} ${isLayout4 ? 'text-[var(--wl4-text)]' : ''}`}>
                      {item.label}
                    </p>
                    {isSelected && (
                      <Check className={`h-4 w-4 shrink-0 ${isLayout4 ? '' : 'text-primary'}`} style={isLayout4 ? { color: 'var(--wl4-accent)' } : undefined} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

